import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import adminApi from '../services/adminApi';

/**
 * Secure version of useUserRole hook
 * All admin operations go through the backend API
 * Service role key is never exposed to the client
 */
export const useUserRole = () => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiHealthy, setApiHealthy] = useState(true);

  // Extract role from user metadata (for current user)
  const extractUserRole = (userData) => {
    const role = userData?.user_metadata?.role;
    // Default to 'model_building_team' if no role is set (matches original behavior)
    return role || 'model_building_team';
  };

  // Initialize role from current user
  useEffect(() => {
    if (user) {
      const role = extractUserRole(user);
      setUserRole(role);
      setLoading(false);
    } else {
      setUserRole(null);
      setLoading(false);
    }
  }, [user]);

  // Check API health on mount
  useEffect(() => {
    const checkApiHealth = async () => {
      const healthy = await adminApi.checkHealth();
      setApiHealthy(healthy);
    };
    checkApiHealth();
  }, []);

  // Update user role via secure API
  const updateUserRole = async (targetUserId, newRole) => {
    try {
      // Check API health first
      if (!apiHealthy) {
        return { 
          success: false, 
          message: 'Admin API is not available. Please contact your administrator.' 
        };
      }

      // Prevent self-demotion for admin users
      if (targetUserId === user?.id && userRole === 'admin' && newRole !== 'admin') {
        return { 
          success: false, 
          message: 'Admins cannot remove their own admin privileges' 
        };
      }

      // Call secure API endpoint
      const result = await adminApi.updateUserRole(targetUserId, newRole);

      // If updating current user, update local state
      if (result.success && targetUserId === user?.id) {
        setUserRole(newRole);
      }

      return result;
    } catch (err) {
      console.error('Error updating user role:', err);
      return { 
        success: false, 
        message: err.message || 'Failed to update role' 
      };
    }
  };

  // Fetch all users via secure API
  const fetchAllUsers = async (page = 1, perPage = 100) => {
    try {
      // Check API health first
      if (!apiHealthy) {
        return { 
          data: null, 
          error: 'Admin API is not available. Please contact your administrator.' 
        };
      }

      const result = await adminApi.fetchUsers(page, perPage);
      return result;
    } catch (err) {
      console.error('Error fetching users:', err);
      return { 
        data: null, 
        error: err.message || 'Failed to fetch users' 
      };
    }
  };

  // Fetch dashboard stats via secure API
  const fetchDashboardStats = async () => {
    try {
      // Check API health first
      if (!apiHealthy) {
        return { 
          stats: null,
          recentUsers: [],
          error: 'Admin API is not available. Please contact your administrator.' 
        };
      }

      const result = await adminApi.fetchStats();
      return result;
    } catch (err) {
      console.error('Error fetching stats:', err);
      return { 
        stats: null,
        recentUsers: [],
        error: err.message || 'Failed to fetch statistics' 
      };
    }
  };

  // Validate admin status via secure API
  const validateAdminStatus = async () => {
    try {
      const result = await adminApi.validateAdmin();
      return result;
    } catch (err) {
      console.error('Error validating admin status:', err);
      return { isAdmin: false, user: null };
    }
  };

  // Permission checking helpers - matches the original role system
  const isAdmin = userRole === 'admin';
  const isOperationsManager = userRole === 'operations_manager';
  const isModelBuildingTeam = userRole === 'model_building_team';
  const isTakeoffs = userRole === 'takeoffs';
  const isOfficeAdmin = userRole === 'office_admin';
  const isDeactivated = userRole === 'deactivated';

  // Legacy role aliases for backward compatibility
  const isMember = isModelBuildingTeam; // Map old member to model_building_team
  const isViewer = false; // Viewer role no longer exists

  const hasPermission = (requiredRole) => {
    // Deactivated users have no permissions
    if (isDeactivated) return false;

    const roleHierarchy = {
      office_admin: 1, // Office admin with limited access
      takeoffs: 2,
      model_building_team: 3,
      operations_manager: 4,
      admin: 5,
    };

    const currentRoleLevel = roleHierarchy[userRole] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    return currentRoleLevel >= requiredRoleLevel;
  };

  const canAccessAdmin = isAdmin && !isDeactivated;
  const canEditProjects = (isAdmin || isOperationsManager || isModelBuildingTeam || isTakeoffs) && !isDeactivated;
  const canViewProjects = (isAdmin || isOperationsManager || isModelBuildingTeam || isTakeoffs) && !isDeactivated;
  const canAccessOperationsDashboards = (isAdmin || isOperationsManager) && !isDeactivated;
  const canAccessModelBuilding = (isAdmin || isOperationsManager || isModelBuildingTeam || isOfficeAdmin) && !isDeactivated;
  const canAccessTakeoffs = (isAdmin || isOperationsManager || isTakeoffs || isOfficeAdmin) && !isDeactivated;
  const canAccessOfficeAdmin = (isAdmin || isOfficeAdmin) && !isDeactivated;

  // Refresh current user's role
  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (user) {
        const role = extractUserRole(user);
        setUserRole(role);
      }
      
      // Check API health
      const healthy = await adminApi.checkHealth();
      setApiHealthy(healthy);
    } catch (err) {
      console.error('Error refreshing user role:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    userRole,
    role: userRole, // Alias for compatibility
    loading,
    error,
    isAdmin,
    isOperationsManager,
    isModelBuildingTeam,
    isTakeoffs,
    isOfficeAdmin,
    isMember, // Legacy alias
    isUser: isMember, // Legacy alias
    isViewer, // Legacy alias (always false)
    isDeactivated,
    hasPermission,
    canAccessAdmin,
    canEditProjects,
    canViewProjects,
    canAccessOperationsDashboards,
    canAccessModelBuilding,
    canAccessTakeoffs,
    canAccessOfficeAdmin,
    updateUserRole,
    fetchAllUsers,
    fetchDashboardStats,
    validateAdminStatus,
    refetch,
    isAdminConfigured: apiHealthy, // API health indicates if admin is configured
    apiHealthy
  };
};

export default useUserRole;